import { FileDetails } from "@/types/types";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import { useEffect, useState } from "react";
import { FileWithPath, useDropzone } from "react-dropzone";
import { CustomButton } from "./Buttons";
import Line from "./Line";
import DeleteIcon from "@/assets/icons/DeleteIconGray.svg";
import UploadIcon from "@/assets/icons/UploadIcon.svg";
import FileIcon from "@/assets/icons/FileIconFill.svg";
import LoadingStrip from "./LoadinStrip";
import { generateFilePathForS3 } from "@/utils/generateFilePathForS3";

interface FileUploadProps {
  videoId?: number;
  onFilesUploaded: (files: FileDetails[]) => void;
  maxFiles?: number;
  defaultFiles?: FileDetails[];
  maxSize?: number; // in bytes
  accept?: { [key: string]: string[] };
  clearFiles?: boolean;
  identifier: string;
  key: string;
  onClear?: () => void;
}

const FileUploadField = ({
  onFilesUploaded,
  maxFiles = 10,
  defaultFiles,
  maxSize = 100 * 1024 * 1024, // 100MB default
  accept = {
    "image/jpeg": [],
    "application/pdf": [],
    "video/mp4": [],
    "image/gif": [],
  },
  clearFiles = false,
  identifier,
  key,
  onClear,
}: FileUploadProps) => {
  const [localFiles, setLocalFiles] = useState<FileDetails[]>(
    defaultFiles || []
  );
  const [showProgress, setShowProgress] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  // Handle clearing files
  useEffect(() => {
    if (clearFiles) {
      setLocalFiles([]);
      if (onClear) onClear();
    }
  }, [clearFiles]);

  // Handle file drop
  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (uploading) return;
    setUploading(true);

    const newFiles = acceptedFiles.slice(0, maxFiles - localFiles.length);
    const updatedFiles = [...localFiles];
    const uniqueNumber = new Date().getTime();

    for (const file of newFiles) {
      const fileDetails: FileDetails = {
        name: `${file.name}-${uniqueNumber.toString()}`,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        webkitRelativePath: file.webkitRelativePath,
        local_url: URL.createObjectURL(file),
        live_url: null,
      };

      setLocalFiles((prevFiles) => [...prevFiles, fileDetails]);

      try {
        setShowProgress(fileDetails.name);
        const filePath = generateFilePathForS3({
          identifier: identifier as string,
          fileName: file.name,
          context: "analysis-videos",
        });

        const uploadedFile = await UploadFileToS3({
          file: fileDetails,
          filePath: filePath,
        });
        const updatedFile = {
          ...fileDetails,
          live_url: uploadedFile.live_url,
          uploading: false,
        };
        updatedFiles.push(updatedFile);

        setLocalFiles((prevFiles) =>
          prevFiles.map((f) => (f.name === fileDetails.name ? updatedFile : f))
        );

        setShowProgress(null);
      } catch (error) {
        setShowProgress(null);
        console.error(`Failed to upload file: ${file.name}`, error);
      }
    }

    setUploading(false);
    onFilesUploaded(updatedFiles);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = localFiles.filter((_, i) => i !== index);
    setLocalFiles(updatedFiles);
    onFilesUploaded(updatedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    maxSize,
    accept,
  });

  return (
    <div>
      {localFiles?.length < maxFiles && (
        <div className="bg-[#F9F9F9] px-4 md:px-6 flex flex-col justify-center items-center border border-[#EAECF0] py-4 rounded-[8px] ">
          <div className="w-full">
            <p className="text-caption2-regular font-medium mb-3">
              Upload files
            </p>

            <div
              {...getRootProps()}
              className="mx-auto gap-6 border border-dashed border-[#9CA6B8] rounded-[12px] md:flex-row flex-col flex justify-center items-center bg-[#FFFFFF] text-center py-6 cursor-pointer px-2"
            >
              <input
                {...getInputProps()}
                className="w-full absolute top-0 left-0 h-full opacity-0"
              />
              <UploadIcon />
              <div>
                <p className="text-caption-large pt-2 text-[#475467]">
                  <span className="text-[#ED5E00] font-semibold">
                    Click to upload
                  </span>{" "}
                  or drag and drop
                </p>
                <p className="text-caption2-regular text-[#475467] pt-1">
                  JPG, PDF, MP4 or GIF (max.500MB)
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mt-4">
        {localFiles.length > 0 && (
          <h5 className="text-sm text-[#344054] font-medium tracking-[-1%] leading-5">
            Uploaded files
          </h5>
        )}
      </div>

      {localFiles.map((file, index) => (
        <div
          key={index}
          className="border-[#EAECF0] flex justify-between items-start border rounded-[8px] px-4 py-4 mt-4"
        >
          <div className="flex w-full gap-x-4 items-start">
            <span>
              <FileIcon />
            </span>
            <div className="w-full">
              <p className="font-medium text-[#344054] text-caption-large">
                {file.name}
              </p>
              <p className="text-[#475467]  text-[13px] leading-5 font-regular">
                {(file.size / 1024).toFixed(2)} KB
              </p>
              <div className="">
                {showProgress === file.name && <LoadingStrip />}
              </div>
              {file.live_url && (
                <a
                  href={file.live_url}
                  target="_blank"
                  className="text-caption-regular font-medium pt-1"
                  rel="noopener noreferrer"
                >
                  View file
                </a>
              )}
            </div>
          </div>
          <div>
            <button
              type="button"
              onClick={() => handleRemoveFile(index)}
              className="text-[#475467]"
            >
              <DeleteIcon />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FileUploadField;
